import { Component, OnInit } from '@angular/core';
import { Condo } from '@api/model/condo';
import { EcondosQuery } from '@api/model/query';
import { CondoService } from '@api/service/condo.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { SessionService } from 'src/app/service/session.service';
import { generateCsv, generateReport } from 'src/app/util/report-generator';

interface TableField {
  value: string;
  label: string;
}

interface SortSettings {
  field: string;
  order: 'asc' | 'desc';
}

enum LoadingStatus {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

@Component({
  selector: 'app-ecamera-page',
  templateUrl: './ecamera-page.component.html',
  styleUrls: ['./ecamera-page.component.scss']
})
export class EcameraPageComponent implements OnInit {
  public loadingStatus: LoadingStatus = LoadingStatus.IDLE;

  public nzPageSize = 50;
  public nzPageIndex = 0;

  public condos: Condo[] = [];
  public totalCondos = 0;

  public tableFields: TableField[] = [
    { label: 'ID', value: '_id' },
    { label: 'Condomínio', value: 'name' },
    { label: 'Whitelabel', value: 'whiteLabel' },
    { label: 'Câmeras', value: 'cameras' },
    { label: 'Valor total', value: 'price' }
  ];

  public sortSetting: SortSettings = {
    field: 'name',
    order: 'asc'
  };

  public generalSearchOptions: TableField[] = [
    { value: 'id', label: 'ID' },
    { value: 'name', label: 'Nome' },
    { value: 'cnpj', label: 'CNPJ' }
  ];

  public planOptions: TableField[] = [
    { value: 'FREE', label: 'FREE / TRIAL' },
    { value: 'PREMIUM', label: 'PREMIUM' }
  ];

  private generalSearchParameter: string = '';
  private generalSearchValue: string = '';
  private searchWhitelabelValue: string = '';
  private searchPlanValue: string = '';

  public showDeleted = false;

  constructor(
    private condoService: CondoService,
    private sessionService: SessionService,
    private notification: NzNotificationService
  ) {}

  public ngOnInit() {
    this.getData();
  }

  public getData(sortBy = 'name') {
    this.loadingStatus = LoadingStatus.LOADING;

    const query: EcondosQuery = {
      $select: 'name whiteLabel eCameraParams',
      $populate: [{ path: 'whiteLabel', select: 'name' }],
      $limit: this.nzPageSize,
      $page: this.nzPageIndex
    };

    this.sortSetting = sortBy
      ? {
          field: sortBy,
          order: this.sortSetting.field === sortBy ? (this.sortSetting.order === 'asc' ? 'desc' : 'asc') : 'asc'
        }
      : { field: 'createdAt', order: 'desc' };

    query.$sort = `${this.sortSetting.order === 'desc' ? '-' : ''}${this.sortSetting.field}`;

    if (this.generalSearchParameter === 'name') {
      query.name = { $regex: this.generalSearchValue, $options: 'i' };
    }

    if (this.generalSearchParameter === 'cnpj') {
      query.cnpj = Number(this.generalSearchValue);
    }

    if (this.generalSearchParameter === 'id') {
      query._id = this.generalSearchValue;
    }

    if (this.searchPlanValue) {
      query['plan.name'] = this.searchPlanValue;
    }

    if (this.searchWhitelabelValue) {
      query['whiteLabel'] = this.searchWhitelabelValue;
    }

    let request;

    if (this.showDeleted) {
      query.$select = query.$select + ' deletedBy deletedAt';

      query.$populate?.push({
        path: 'deletedBy',
        select: 'firstName lastName'
      });

      request = this.condoService.getDeletedCondos(query);
    } else {
      request = this.condoService.getCondosWithCount(query);
    }

    request.subscribe({
      next: response => {
        this.condos = response.data;
        this.totalCondos = Number(response.count);
        this.loadingStatus = LoadingStatus.SUCCESS;
      },
      error: error => {
        console.error(error);
        this.notification.error(
          'Erro ao listar condomínios',
          error?.error?.message || error?.message || error || 'Não foi possível listar os condomínios. Recarregue a página'
        );
        this.loadingStatus = LoadingStatus.ERROR;
      }
    });
  }

  public refreshIndexChange(value: number) {
    this.nzPageIndex = --value;
    this.getData();
  }

  public refreshSizeChange(value: number) {
    this.nzPageSize = value;
    this.getData();
  }

  public getSortedData(field: string) {
    this.sortSetting.field = field;
    this.getData(field);
  }

  public async exportAsPDF(fields: TableField[], condos: Condo[]) {
    const data = this.formatDataForReport(condos);
    try {
      await generateReport('Lista_de_cameras_analiticas', fields, data, this.sessionService.user);
    } catch {
      this.notification.error('Relatório', 'Não foi possível gerar o PDF. Tente novamente.');
    }
  }

  public exportAsCsv(fields: TableField[], condos: Condo[]) {
    const data = this.formatDataForReport(condos);
    const encodeUri = generateCsv(fields, data);
    const link = document.createElement('a');
    link.setAttribute('href', encodeUri);
    link.setAttribute('download', `Lista_de_cameras_analiticas.csv`);
    link.click();
  }

  public handleGeneralSearchFilter(data: any) {
    const { value, parameter } = data;
    this.generalSearchValue = value;
    this.generalSearchParameter = parameter;
    this.getData();
  }

  public closeGeneralSearchFilter() {
    this.generalSearchValue = '';
    this.generalSearchParameter = '';
    this.getData();
  }

  public handleWhitelabelFilter(value: string) {
    this.searchWhitelabelValue = value;
    this.getData();
  }

  public closeWhitelabelFilter() {
    this.searchWhitelabelValue = '';
    this.getData();
  }

  public handleSearchPlanFilter(value: string) {
    this.searchPlanValue = value;
    this.getData();
  }

  public closeSearchPlanFilter() {
    this.searchPlanValue = '';
    this.getData();
  }

  public toggleShowDeleted() {
    this.showDeleted = !this.showDeleted;
    this.getData();
  }

  private formatDataForReport(condos: Condo[]) {
    return condos.map(condo => {
      const isEcameraEnabled = condo.eCameraParams?.enabled || false;
      const camerasPrice = (condo.eCameraParams?.pricePerCamera || 0) * (condo.eCameraParams?.camerasLimit || 0);

      return {
        _id: condo._id,
        name: condo.name,
        whiteLabel: condo.whiteLabel?.name || 'Nenhum',
        cameras: isEcameraEnabled ? condo.eCameraParams?.camerasLimit || '0' : '0',
        price: isEcameraEnabled ? camerasPrice.toFixed(2) : '0.00'
      };
    });
  }
}
